<!-- eslint-disable @typescript-eslint/no-non-null-assertion -->
<!-- eslint-disable no-console -->
<script lang="ts" setup>
import AppState from '@/states/app'
import { XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UICheckbox,
  UIModal,
  UIModalFooter,
  UIRadio,
} from '@gohighlevel/ghl-ui'
import { onMounted, PropType, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import oneWaySync from '../../assets/oneWaySync.webp'
import smartSync from '../../assets/smartSync.webp'
import twoWaySync from '../../assets/twoWaySync.webp'

const { t } = useI18n()

enum SYNC_TYPE {
  DEFAULT = 'default',
  TWOWAY = 'twoway',
  ONEWAY = 'oneway',
  SMART = 'smart',
  DISABLE_TRIGGER = 'disable_trigger',
}

const emit = defineEmits(['onSave', 'onCancel'])

const props = defineProps({
  showModal: Boolean,
  onClickClose: Function,
  formData: Object as PropType<{
    selectedCalendar: string
    syncOption: string
    shouldExecuteAutomation: boolean
    reserveService: boolean
    category: string
    service: string
    price: number | undefined
    currencyCode: string
  }>,
  emitSave: Function,
})

const state = reactive({
  canTriggerRun: false,
  defaultCalendarServiceId: null,
  selectedSync: props.formData?.syncOption || SYNC_TYPE.ONEWAY,
  shouldExecuteAutomation: props.formData?.shouldExecuteAutomation || false,
  isSaveButtonDisabled: AppState.isConnectionSyncLoading,
})

watch(
  () => props.formData?.syncOption,
  (newValue, oldValue) => {
    state.selectedSync = (newValue as SYNC_TYPE) || SYNC_TYPE.ONEWAY
  }
)

watch(
  () => props.formData?.shouldExecuteAutomation,
  (newValue, oldValue) => {
    state.shouldExecuteAutomation = newValue || false
  }
)

onMounted(() => {
  AppState.isConnectionSyncLoading = false
})

const syncData = {
  [SYNC_TYPE.ONEWAY]: {
    title: t('calendar_advanced.connections.default_one_way'),
    description: t('calendar_advanced.connections.one_way_sync_desc'),
    isRecommended: true,
    showTooltip: true,
    id: SYNC_TYPE.ONEWAY,
    image: oneWaySync,
  },
  [SYNC_TYPE.TWOWAY]: {
    title: t('calendar_advanced.connections.two_way_sync'),
    description: t('calendar_advanced.connections.two_way_sync_desc'),
    isRecommended: false,
    showTooltip: true,
    id: SYNC_TYPE.TWOWAY,
    image: twoWaySync,
  },
  [SYNC_TYPE.SMART]: {
    title: t('calendar_advanced.connections.smart_sync'),
    description: t('calendar_advanced.connections.smart_sync_desc'),
    isRecommended: false,
    showTooltip: true,
    id: SYNC_TYPE.SMART,
    image: smartSync,
  },
} as const

const onClickSyncType = (val: string) => {
  state.selectedSync = val
  props.formData!.syncOption = val
}
</script>

<template>
  <UIModal :show="showModal" :width="520">
    <template #header>
      <div class="flex w-full flex-row items-start justify-between">
        <div>
          <div class="text-[18px] font-medium leading-6 text-gray-900">
            {{ $t('calendar_advanced.connections.sync_preferences') }}
          </div>
        </div>
        <XCloseIcon
          class="h-5 w-5 cursor-pointer text-gray-500"
          @click="props.onClickClose?.()"
        />
      </div>
      <div class="mt-2 w-[80%] text-[13px] font-normal leading-5 text-gray-600">
        {{ $t('calendar_advanced.connections.sync_preferences_desc') }}
      </div>
    </template>

    <div class="mb-3 mt-4 flex flex-col rounded-lg">
      <img
        :src="syncData[state.selectedSync].image"
        :alt="t('alt_text.sync_icon')"
        class="mb-3"
      />
      <div class="mb-3 flex w-full flex-row items-center justify-center">
        <div class="text-[12px] font-medium leading-6 text-gray-500">
          {{ syncData[state.selectedSync].title }}
        </div>
      </div>
      <div
        v-for="(item, index) in syncData"
        :key="index"
        :value="item.id"
        :disabled="false"
        :class="
          item.id === state.selectedSync
            ? 'calendars-user-conflict-selected-sync-container'
            : 'calendars-user-conflict-sync-container'
        "
        role="button"
        :aria-label="t('alt_text.sync_type')"
        @click="onClickSyncType(item.id)"
        @keydown="onClickSyncType(item.id)"
      >
        <UIRadio
          :id="item.id"
          v-model:value="state.selectedSync"
          :name="item.id"
          :checked="state.selectedSync === item.id"
          @change="onClickSyncType(item.id)"
        />
        <div>
          <div class="mb-1 flex flex-row items-center gap-2">
            <div class="tetx-gray-900 text-[14px] font-medium leading-6">
              {{ item.title }}
            </div>
            <div
              v-if="item.isRecommended"
              class="w-auto rounded-2xl bg-primary-50 px-[8px] py-[2px] text-[12px] font-medium text-primary-700"
            >
              {{ $t('calendar_advanced.connections.recommended') }}
            </div>
          </div>
          <div class="text-[12px] font-normal leading-5 text-gray-500">
            {{ item.description }}
          </div>
          <div
            v-if="
              item.id === SYNC_TYPE.TWOWAY &&
              state.selectedSync === SYNC_TYPE.TWOWAY
            "
            class="mb-2 mt-2 h-[1px] w-full bg-gray-200"
          />
          <div
            v-if="
              item.id === SYNC_TYPE.TWOWAY &&
              state.selectedSync === SYNC_TYPE.TWOWAY
            "
            class="flex flex-col"
          >
            <UICheckbox
              id="automation-checkbox"
              :checked="
                state.shouldExecuteAutomation &&
                state.selectedSync === SYNC_TYPE.TWOWAY
              "
              class="w-[200px]"
              @update:checked="
                (val: boolean) => {
                  state.shouldExecuteAutomation = val
                  props.formData!.shouldExecuteAutomation = val
                }
              "
            >
              <div class="text-[14px] text-gray-900">
                {{ $t('calendar_advanced.connections.execute_automation') }}
              </div>
            </UICheckbox>
            <div class="text-[13px] text-gray-600">
              {{ $t('calendar_advanced.connections.fire_workflow') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <UIModalFooter
      id="priamryCalendarFooter"
      :positive-text="$t('calendar_advanced.connections.save')"
      :negative-text="$t('calendar_advanced.connections.cancel')"
      :loading="AppState.isConnectionSyncLoading"
      :disabled="AppState.isConnectionSyncLoading"
      :type="'primary'"
      @negative-click="props.onClickClose?.()"
      @positive-click="props.emitSave?.()"
    />
  </UIModal>
</template>

<style scoped lang="scss">
.calendar-contact-creation-divider {
  border: 0.8px solid var(--gray-200);
  width: 100%;
  margin: 10px 0px 10px 0px;
}
.calendars-user-conflict-sync-container {
  padding: 20px 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: row-reverse;
  width: auto;
  gap: 20px;
  border-radius: 8px;
  border: 1px solid var(--gray-200);
  cursor: pointer;
}

.calendars-user-conflict-selected-sync-container {
  padding: 20px 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: row-reverse;
  width: auto;
  gap: 20px;
  border-radius: 8px;
  border: 1px solid var(--primary-600);
  cursor: pointer;
}
</style>
